import { DateService } from "../services/DateService.js";
/**
 * Parses the route with the specified custom name and params out of the specified set of routes.
 *
 * @param params A mapping with the key values to replace in the route url template or a single value or an array, if the correct params order is specified
 * @param locale The locale to prefix on the resolved route path.
 * @param absoluteUrl The url to prepend on the route
 */
export const parseRoute = (routes, name, params = null, locale = null, absoluteUrl = null) => {
    const names = name.split('.');
    let route = routes[names[0]];
    if (!route) {
        throw new Error(`Route "${name}" is not defined.`);
    }
    // If route is of type RoutesConfig
    if (typeof route === 'object') {
        for (let i = 1; i < names.length; i++) {
            route = route[names[i]];
            if (!route) {
                throw new Error(`Route "${name}" is not defined.`);
            }
        }
    }
    // If we have an object, we get the index page
    if (typeof route === 'object') {
        route = route.index;
    }
    // Make sure we have a valid route name
    if (!route || typeof route !== 'string') {
        throw new Error(`Route "${name}" is not a valid route name.`);
    }
    if (params && Array.isArray(params)) {
        // If the params are specified as array, we just replace the route params in the specified order of the array by ignoring the names of the route params
        params.forEach((param) => {
            route = route.replace(new RegExp('{(.*?)}', 'm'), param);
        });
    }
    else if (params && typeof params === 'object') {
        // If the params are specified as object, we replace each item with its corresponding param key
        Object.keys(params).forEach(key => {
            route = route.replace(`{${key}}`, params[key]);
        });
    }
    else if (params) {
        // If the params is specified as string, we just replace the one route param we expect on the route
        route = route.replace(new RegExp('{(.*?)}', 'm'), params);
    }
    // Now replace all optional route params, e.g., {param?}
    route = route.replace(new RegExp('\\/{[^}]+\\?}', 'gm'), '');
    // For server side, we have to remove the 'api' prefix for our api requests
    // @ts-ignore
    if (!process.client) {
        route = route.replace('/api', '');
    }
    // Remove the prefixed slash to be consistent
    route = (route.startsWith('/') ? route.substring(1) : route);
    if (absoluteUrl) {
        absoluteUrl = absoluteUrl.endsWith('/') ? absoluteUrl.slice(0, -1) : absoluteUrl;
        route = `${absoluteUrl}/` + (locale ? `${locale}/` : '') + route;
        return encodeURI(route);
    }
    return (locale ? `/${locale}/` : '/') + encodeURI(route);
};
/**
 * Parses the route with the specified vue router name and params.
 *
 * @param router
 * @param name The name generated by Vue router.
 * @param params The params object to pass to the route. This can be used to pass props to pages and templates as well.
 * @param locale The locale to use for the route name.
 * @param absoluteUrl The url to prepend on the route
 * @returns string|object
 */
export function parseVueNamedRoute(router, name, params = null, locale = null, absoluteUrl = null) {
    let routeName = name;
    if (locale) {
        routeName = `${routeName}___${locale}`; // Use route generated by nuxt-i18n
    }
    const routeObject = {
        name: routeName,
        ...params
    };
    if (absoluteUrl) {
        const path = router.resolve(routeObject).href;
        absoluteUrl = absoluteUrl.endsWith('/') ? absoluteUrl.slice(0, -1) : absoluteUrl;
        return `${absoluteUrl}${path}`;
    }
    return routeObject;
}
/**
 * Gets the number of decimals of the specified float number.
 *
 * @param number
 */
export function parseNumberOfDecimals(number) {
    if (!isFinite(number))
        return 0;
    let e = 1, precision = 0;
    while (Math.round(number * e) / e !== number) {
        e *= 10;
        precision++;
    }
    return precision;
}
/**
 * Calculates the distance between the two lat lngs in kilometers.
 *
 * @param latLng1
 * @param latLng2
 * @returns {number}
 */
export function parseDistance(latLng1, latLng2) {
    const lat1 = latLng1.lat;
    const lng1 = latLng1.lng;
    const lat2 = latLng2.lat;
    const lng2 = latLng2.lng;
    const earthRadius = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadius * c; // Distance in km
}
/**
 * Transforms the specified degrees in radius.
 *
 * @param deg
 * @returns {number}
 */
export function deg2rad(deg) {
    return deg * (Math.PI / 180);
}
export function parseUrlHost(url) {
    const match = url.match(/^(https?:)\/\/(([^:/?#]*)(?::([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
    return (match === null || match === void 0 ? void 0 : match.length) ? match[2] : url;
}
/**
 * Extracts the urls of the specified text string.
 *
 * @param {string} text
 * @param includeWrapped Whether to match urls that are (probably) wrapped by tags.
 * @returns {array}
 */
export function extractUrls(text, includeWrapped = true) {
    if (!text || !text.length) {
        return [];
    }
    const matchPrefix = includeWrapped ? `(?:^|[^href"'])` : `(?:^|[^"'>https://])`; // Note that negative lookahead does not work on Safari
    const regex = new RegExp(`${matchPrefix}((https?:\\/\\/[^\\s!.]+.|https?:\\/\\/|www.[^\\s!.]+.|www.){1}(\\S+\\.\\w{2,})(\\/\\w+[\\w-\\/]+(\\.html)?)?([?#&]{1}[\\w=&#+-]+)?)`, 'gim');
    const links = [];
    let regexExtract;
    while ((regexExtract = regex.exec(text)) !== null) {
        // @ts-ignore
        links.push(regexExtract[1]); // The extracted url is the first matched group
    }
    // Filter duplicates
    return links && links.length ? links.filter((link, index, self) => self.indexOf(link) === index) : [];
}
export function parseBoolean(value) {
    return value && (value === 'true' || value === '1' || value === true || value === 1);
}
export function parseForInputType(type, value = null) {
    if (value === null || value === undefined) {
        return null;
    }
    if (Array.isArray(value)) {
        return value.map(itValue => parseForInputType(itValue)).join(', ');
    }
    if (type && type.is_boolean) {
        return parseBoolean(value);
    }
    if (type && type.value === 'date') {
        return (new DateService(value)).format('LL');
    }
    return value;
}
//# sourceMappingURL=parse-helpers.js.map